import { useState, useRef, type ChangeEvent as ReactChangeEvent, type SyntheticEvent as ReactSyntheticEvent } from 'react'
import { CheckIcon, ImageIcon, UserIcon, XIcon } from '../icons'
import type { User } from '../types'
import { API_URL, getToken } from '../api'
import { avatarUrl as getAvatarUrl } from '../utils/avatar'

export function ProfileModal({ user, updateUser, onClose }: { user: User | null; updateUser: (u: Partial<User>) => void; onClose: () => void }) {
  const [displayName, setDisplayName] = useState(user?.display_name || '')
  const [statusMessage, setStatusMessage] = useState(user?.status_message || '')
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null) // data URL from file selection
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const currentAvatarUrl = getAvatarUrl(user)

  const handleFileSelect = (e: ReactChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Preview
    const reader = new FileReader()
    reader.onload = (ev) => setAvatarPreview(String(ev.target?.result ?? ''))
    reader.readAsDataURL(file)
    // Store file for upload
    Object.assign(e.target, { _file: file })
  }

  const handleSave = async (e: ReactSyntheticEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const fileInput = fileInputRef.current
      const file = fileInput?.files?.[0]

      let res
      if (file) {
        // Multipart upload with avatar
        const formData = new FormData()
        formData.append('display_name', displayName)
        formData.append('status_message', statusMessage)
        formData.append('avatar', file)
        const token = getToken()
        res = await fetch(`${API_URL}/api/profile`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        })
      } else {
        // JSON only (display_name + status_message)
        const token = getToken()
        res = await fetch(`${API_URL}/api/profile`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ display_name: displayName, status_message: statusMessage }),
        })
      }

      const data = await res.json()
      if (res.ok) {
        updateUser({ display_name: data.display_name, avatar_url: data.avatar_url, status_message: data.status_message })
        onClose()
      } else {
        setError(data.error || 'Erro ao salvar')
      }
    } catch {
      setError('Erro de conexão')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Perfil</h2>
          <button className="icon-btn" onClick={onClose}><XIcon size={18} /></button>
        </div>

        <form onSubmit={handleSave} className="profile-modal-form">
          {/* Avatar */}
          <div className="profile-avatar-section">
            <div className="profile-avatar-large" onClick={() => fileInputRef.current?.click()}>
              {avatarPreview || currentAvatarUrl ? (
                <img src={avatarPreview || currentAvatarUrl} alt="Avatar" className="avatar-img" />
              ) : (
                <UserIcon size={48} />
              )}
            </div>
            <button type="button" className="btn-ghost" onClick={() => fileInputRef.current?.click()}>
              <ImageIcon size={14} /> Alterar foto
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>

          {/* Display name */}
          <div className="profile-field">
            <label>Nome de exibição</label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Seu nome"
              autoFocus
            />
          </div>

          {/* Status message */}
          <div className="profile-field">
            <label>Frase de aviso</label>
            <div className="profile-input-with-action">
              <input
                type="text"
                value={statusMessage}
                onChange={e => setStatusMessage(e.target.value)}
                placeholder="Ex: Em reunião, volto às 15h"
                maxLength={255}
              />
              {statusMessage && (
                <button
                  type="button"
                  className="profile-input-clear"
                  onClick={() => setStatusMessage('')}
                  title="Apagar frase de aviso"
                  aria-label="Apagar frase de aviso"
                >
                  <XIcon size={14} />
                </button>
              )}
            </div>
            <small style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              Aparece abaixo do seu nome na sidebar. Limpe o campo e salve para remover.
            </small>
          </div>

          {/* Email (read-only) */}
          <div className="profile-field">
            <label>Email</label>
            <input type="text" value={user?.email || ''} disabled />
          </div>

          {error && <div className="profile-error">{error}</div>}

          <div className="profile-modal-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              <CheckIcon size={16} /> {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  )
}
